import React, { useState, useCallback, useMemo } from 'react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';
import { 
  Search, 
  Copy, 
  RefreshCw, 
  Check, 
  AlertCircle,
  Info,
  BookOpen,
  Zap,
  Target,
  Code
} from 'lucide-react';

interface Match {
  match: string;
  index: number;
  groups: string[];
  namedGroups: { [key: string]: string };
}

interface RegexPattern {
  name: string;
  pattern: string;
  description: string;
  example: string;
}

function RegexTester() {
  const [activeTab, setActiveTab] = useState<'tester' | 'creator'>('tester');
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState('g');
  const [testString, setTestString] = useState('');
  const [matches, setMatches] = useState<Match[]>([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [replaceText, setReplaceText] = useState('');
  const [replaceResult, setReplaceResult] = useState('');

  // Common regex patterns for the creator
  const commonPatterns: RegexPattern[] = [
    {
      name: 'Email Address',
      pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}',
      description: 'Matches most common email address formats',
      example: 'user@example.com, test.email+tag@domain.co.uk'
    },
    {
      name: 'Phone Number (US)',
      pattern: '\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})',
      description: 'Matches US phone numbers in various formats',
      example: '(555) 123-4567, 555-123-4567, 555.123.4567'
    },
    {
      name: 'URL',
      pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)',
      description: 'Matches HTTP and HTTPS URLs',
      example: 'https://www.example.com, http://test.co.uk/path?query=value'
    },
    {
      name: 'IP Address (IPv4)',
      pattern: '\\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b',
      description: 'Matches valid IPv4 addresses',
      example: '192.168.1.1, 10.0.0.1, 255.255.255.255'
    },
    {
      name: 'Date (MM/DD/YYYY)',
      pattern: '\\b(0?[1-9]|1[0-2])\\/(0?[1-9]|[12][0-9]|3[01])\\/(19|20)\\d{2}\\b',
      description: 'Matches dates in MM/DD/YYYY format',
      example: '01/15/2024, 12/31/2023, 5/4/2022'
    },
    {
      name: 'Credit Card Number',
      pattern: '\\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|3[0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12})\\b',
      description: 'Matches major credit card number formats',
      example: '4111111111111111, 5555555555554444, 378282246310005'
    },
    {
      name: 'Hexadecimal Color',
      pattern: '#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\\b',
      description: 'Matches hexadecimal color codes',
      example: '#FF5733, #333, #00AABB'
    },
    {
      name: 'Social Security Number',
      pattern: '\\b\\d{3}-\\d{2}-\\d{4}\\b',
      description: 'Matches US Social Security Numbers',
      example: '123-45-6789, 987-65-4321'
    },
    {
      name: 'Username/Handle',
      pattern: '@[a-zA-Z0-9_]{1,15}\\b',
      description: 'Matches social media usernames/handles',
      example: '@username, @user_123, @test'
    },
    {
      name: 'Password (Strong)',
      pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$',
      description: 'Validates strong passwords (8+ chars, uppercase, lowercase, number, special char)',
      example: 'Password123!, MyStr0ng@Pass'
    }
  ];

  const testRegex = useCallback(() => {
    if (!pattern) {
      setMatches([]);
      setError('');
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const foundMatches: Match[] = [];
      
      if (flags.includes('g')) {
        let match;
        while ((match = regex.exec(testString)) !== null) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
            namedGroups: match.groups || {}
          });
          
          // Prevent infinite loop on zero-length matches
          if (match.index === regex.lastIndex) {
            regex.lastIndex++;
          }
        }
      } else {
        const match = regex.exec(testString);
        if (match) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
            namedGroups: match.groups || {}
          });
        }
      }
      
      setMatches(foundMatches);
      setError('');
    } catch (err: any) {
      setError(`Invalid regex: ${err.message}`);
      setMatches([]);
    }
  }, [pattern, flags, testString]);

  const performReplace = useCallback(() => {
    if (!pattern || !testString) {
      setReplaceResult('');
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const result = testString.replace(regex, replaceText);
      setReplaceResult(result);
    } catch (err: any) {
      setReplaceResult(`Error: ${err.message}`);
    }
  }, [pattern, flags, testString, replaceText]);

  // Auto-test when inputs change
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      testRegex();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [testRegex]);

  // Auto-replace when inputs change
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      performReplace();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [performReplace]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const clearAll = () => {
    setPattern('');
    setTestString('');
    setReplaceText('');
    setReplaceResult('');
    setMatches([]);
    setError('');
    setFlags('g');
  };

  const loadPattern = (regexPattern: RegexPattern) => {
    setPattern(regexPattern.pattern);
    setTestString(regexPattern.example);
    setActiveTab('tester');
  };

  const highlightMatches = useMemo(() => {
    if (!testString || matches.length === 0) {
      return testString;
    }

    let result = testString;
    let offset = 0;

    // Sort matches by index to process them in order
    const sortedMatches = [...matches].sort((a, b) => a.index - b.index);

    sortedMatches.forEach((match, i) => {
      const startTag = `<mark style="background-color: var(--lyra-color-warning-bg); color: var(--lyra-color-warning); padding: 2px 4px; border-radius: 3px; font-weight: bold;">`;
      const endTag = '</mark>';
      
      const start = match.index + offset;
      const end = start + match.match.length;
      
      result = result.slice(0, start) + startTag + result.slice(start, end) + endTag + result.slice(end);
      offset += startTag.length + endTag.length;
    });

    return result;
  }, [testString, matches]);

  const flagOptions = [
    { key: 'g', label: 'Global', description: 'Find all matches' },
    { key: 'i', label: 'Ignore Case', description: 'Case insensitive matching' },
    { key: 'm', label: 'Multiline', description: '^$ match line breaks' },
    { key: 's', label: 'Dot All', description: '. matches newlines' },
    { key: 'u', label: 'Unicode', description: 'Unicode support' },
    { key: 'y', label: 'Sticky', description: 'Match from lastIndex' }
  ];

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="Regex Tester" category="dev-utils" />
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Search size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>Regex Tester & Creator</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Test regular expressions, create patterns, and validate text matching with real-time feedback</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('tester')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tester'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Target size={18} />
                Regex Tester
              </div>
            </button>
            <button
              onClick={() => setActiveTab('creator')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'creator'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap size={18} />
                Pattern Creator
              </div>
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'tester' ? (
        // Tester Content
        <>
          {/* Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Regex Pattern Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Regular Expression Pattern
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    placeholder="Enter your regex pattern..."
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white pr-12"
                  />
                  <div className="absolute right-3 top-3 text-gray-400">
                    <Code size={18} />
                  </div>
                </div>
              </div>

              {/* Flags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Flags
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {flagOptions.map((flag) => (
                    <label key={flag.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={flags.includes(flag.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFlags(prev => prev + flag.key);
                          } else {
                            setFlags(prev => prev.replace(flag.key, ''));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-300" title={flag.description}>
                        {flag.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                <RefreshCw size={16} />
                Clear All
              </button>

              {pattern && (
                <button
                  onClick={() => copyToClipboard(pattern)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                    copied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy Pattern'}
                </button>
              )}
            </div>
          </div>

          {/* Status */}
          {error && (
            <div className="mb-6 p-4 rounded-lg flex items-center gap-3 bg-red-50 border border-red-200 text-red-700">
              <AlertCircle size={20} />
              <div>{error}</div>
            </div>
          )}

          {/* Test String and Results */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Test String Input */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Search size={20} />
                  Test String
                </h2>
              </div>
              <div className="p-4">
                <textarea
                  value={testString}
                  onChange={(e) => setTestString(e.target.value)}
                  placeholder="Enter text to test against your regex pattern..."
                  className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white resize-none"
                />
              </div>
            </div>

            {/* Highlighted Results */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Target size={20} />
                  Highlighted Matches
                  {matches.length > 0 && (
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {matches.length} match{matches.length !== 1 ? 'es' : ''}
                    </span>
                  )}
                </h2>
              </div>
              <div className="p-4">
                <div 
                  className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-50 dark:bg-gray-700 dark:text-white overflow-auto whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: highlightMatches }}
                />
              </div>
            </div>
          </div>

          {/* Matches Details */}
          {matches.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Match Details</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {matches.map((match, index) => (
                  <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        Match {index + 1}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Position: {match.index}
                      </span>
                    </div>
                    <div className="font-mono text-sm bg-yellow-100 dark:bg-yellow-900/20 p-2 rounded border-l-4 border-yellow-500">
                      "{match.match}"
                    </div>
                    {match.groups.length > 0 && (
                      <div className="mt-2">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Groups:</div>
                        {match.groups.map((group, groupIndex) => (
                          <div key={groupIndex} className="text-sm font-mono text-gray-600 dark:text-gray-400">
                            Group {groupIndex + 1}: "{group}"
                          </div>
                        ))}
                      </div>
                    )}
                    {Object.keys(match.namedGroups).length > 0 && (
                      <div className="mt-2">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Named Groups:</div>
                        {Object.entries(match.namedGroups).map(([name, value]) => (
                          <div key={name} className="text-sm font-mono text-gray-600 dark:text-gray-400">
                            {name}: "{value}"
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Replace Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <RefreshCw size={20} />
              Find & Replace
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Replace With
                </label>
                <input
                  type="text"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Replacement text (use $1, $2 for groups)..."
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Result
                </label>
                <div className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-50 dark:bg-gray-700 dark:text-white min-h-[48px] flex items-center">
                  {replaceResult || 'Replacement result will appear here...'}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Creator Content
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BookOpen size={20} />
            Common Regex Patterns
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Choose from these commonly used regex patterns and test them with sample data.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {commonPatterns.map((regexPattern, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-blue-500 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900 dark:text-white">{regexPattern.name}</h4>
                  <button
                    onClick={() => loadPattern(regexPattern)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    Test
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{regexPattern.description}</p>
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Pattern:</div>
                  <code className="text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded block font-mono break-all">
                    {regexPattern.pattern}
                  </code>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Example matches:</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 italic">
                    {regexPattern.example}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Reference */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center gap-2">
              <Info size={18} />
              Quick Reference
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-medium text-blue-800 dark:text-blue-200 mb-1">Character Classes:</div>
                <div className="space-y-1 text-blue-700 dark:text-blue-300">
                  <div><code>.</code> - Any character</div>
                  <div><code>\d</code> - Digit (0-9)</div>
                  <div><code>\w</code> - Word character</div>
                  <div><code>\s</code> - Whitespace</div>
                </div>
              </div>
              <div>
                <div className="font-medium text-blue-800 dark:text-blue-200 mb-1">Quantifiers:</div>
                <div className="space-y-1 text-blue-700 dark:text-blue-300">
                  <div><code>*</code> - 0 or more</div>
                  <div><code>+</code> - 1 or more</div>
                  <div><code>?</code> - 0 or 1</div>
                  <div><code>{`{n,m}`}</code> - n to m times</div>
                </div>
              </div>
              <div>
                <div className="font-medium text-blue-800 dark:text-blue-200 mb-1">Anchors:</div>
                <div className="space-y-1 text-blue-700 dark:text-blue-300">
                  <div><code>^</code> - Start of string</div>
                  <div><code>$</code> - End of string</div>
                  <div><code>\b</code> - Word boundary</div>
                  <div><code>()</code> - Capture group</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegexTester;
